import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import {
  useInvitationBySlug,
  usePublicInvitationFamily,
  usePublicInvitationGallery,
  usePublicInvitationGifts,
  useGuestByAccessCode,
  useUpdateRSVP,
  markGuestViewed,
} from '@/hooks/useInvitations';
import { FAMILY_ROLE_LABELS, FamilyRole } from '@/types/invitation';
import InvitationV2 from '@/invitation/render/InvitationV2';
import GuestAlbumSection from '@/invitation/album/GuestAlbumSection';
import { buildPayload, AdapterInvitation, AdapterEvent } from '@/invitation/payload/adapter';
import { parseCalendarDate } from '@/lib/parseCalendarDate';
import { themeClass } from '@/invitation/payload/styles';

const PublicInvitation = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const accessCode = searchParams.get('code') || '';
  const [entered, setEntered] = useState(false);

  const { data: invitation, isLoading } = useInvitationBySlug(slug || '');
  const invitationId = invitation?.id || '';

  const { data: family } = usePublicInvitationFamily(invitationId);
  const { data: gallery } = usePublicInvitationGallery(invitationId);
  const { data: gifts } = usePublicInvitationGifts(invitationId);
  const { data: guest } = useGuestByAccessCode(accessCode);
  const updateRSVP = useUpdateRSVP();

  // 008 · US7 — marca "visto" al resolver al invitado (fire-and-forget). Al salir, envía la
  // duración en segundos (best-effort). No bloquea el render → no afecta el LCP.
  const guestId = guest?.id;
  useEffect(() => {
    if (!accessCode || !guestId) return;
    const start = Date.now();
    void markGuestViewed(accessCode);
    const onLeave = () => {
      void markGuestViewed(accessCode, Math.round((Date.now() - start) / 1000));
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, [accessCode, guestId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <Heart className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-display font-bold mb-2">Invitación no encontrada</h1>
        <p className="text-muted-foreground">
          Este link no es válido o la invitación ya no está disponible.
        </p>
      </div>
    );
  }

  // 004: la invitación publicada es el snapshot público (anon-readable). Nombres/fecha
  // salen de sus columnas (mantenidas en sync por useUpsertEvent). El invitado 🔗 viene
  // de access_code (002). Familia y galería se inyectan en el payload.
  const inv = invitation as unknown as AdapterInvitation & {
    bride_name?: string;
    groom_name?: string;
    wedding_date?: string;
    couple_name_order?: string;
    sections?: Record<string, unknown> | null;
    guest_photos_enabled?: boolean | null;
    guest_photos_auto_approve?: boolean | null;
  };
  const priorSections = (inv.sections ?? {}) as Record<string, unknown>;
  const priorFotos = (priorSections.fotos ?? {}) as Record<string, unknown>;
  const adapterInvitation: AdapterInvitation = {
    ...inv,
    sections: {
      ...priorSections,
      familia: (family ?? []).map((f) => ({
        rol: FAMILY_ROLE_LABELS[f.role as FamilyRole] ?? f.role,
        nombre: f.name,
      })),
      fotos: { ...priorFotos, galeria: (gallery ?? []).map((g) => g.photo_url) },
      regalosSugerencias: (gifts ?? []).map((g) => ({
        nombre: g.gift_name,
        precio: g.gift_price ?? undefined,
        descripcion: g.gift_description ?? undefined,
      })),
    },
  };
  const adapterEvent: AdapterEvent = {
    bride_name: inv.bride_name,
    groom_name: inv.groom_name,
    wedding_date: inv.wedding_date,
    couple_name_order: inv.couple_name_order, // 008 · US10 (snapshot)
    // La fecha límite mostrada deriva del evento; en el público viaja con el invitado (RPC).
    rsvp_deadline: guest?.rsvp_deadline,
  };
  const payload = buildPayload({
    invitation: adapterInvitation,
    event: adapterEvent,
    guest: guest
      ? { guest_name: guest.guest_name, display_label: guest.display_label, passes_count: guest.passes_count }
      : null,
  });

  const handleRsvp = async (formData: unknown) => {
    if (!accessCode) {
      toast.error('Necesitas un link de invitación válido');
      return;
    }
    const d = (formData ?? {}) as { attending?: string; count?: number; message?: string };
    await updateRSVP.mutateAsync({
      accessCode,
      data: {
        rsvp_status: d.attending === 'si' ? 'confirmed' : 'declined',
        rsvp_guests_count: d.count ?? 0,
        rsvp_message: d.message ?? null,
      },
    });
  };

  // 008 · US1 — RSVP cerrado si la fecha límite del evento ya pasó; contacto del coordinador.
  const rsvpClosed = !!guest?.rsvp_deadline && new Date(guest.rsvp_deadline) < new Date();
  const coordDigits = (guest?.coordinator_phone ?? '').replace(/\D/g, '');
  // El coordinador lleva varias bodas: el mensaje identifica novios, fecha y quién escribe.
  const novios =
    inv.couple_name_order === 'groom_first'
      ? `${inv.groom_name ?? ''} y ${inv.bride_name ?? ''}`
      : `${inv.bride_name ?? ''} y ${inv.groom_name ?? ''}`;
  const fechaTxt = inv.wedding_date
    ? (parseCalendarDate(inv.wedding_date) ?? new Date(inv.wedding_date)).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const quien = guest?.display_label || guest?.guest_name || '';
  const coordinadorUrl = coordDigits
    ? `https://wa.me/${coordDigits}?text=${encodeURIComponent(
        `Hola, soy ${quien}, invitado/a a la boda de ${novios}` +
          (fechaTxt ? ` (${fechaTxt})` : '') +
          '. El plazo para confirmar asistencia ya cerró y me gustaría coordinar mi asistencia.',
      )}`
    : undefined;

  // 009 — Álbum compartido: solo si está encendido y hay código de invitado válido (privacidad).
  const albumSlot =
    inv.guest_photos_enabled && accessCode && invitationId ? (
      <GuestAlbumSection
        accessCode={accessCode}
        invitationId={invitationId}
        autoApprove={inv.guest_photos_auto_approve ?? true}
      />
    ) : undefined;

  return (
    <>
      <InvitationV2
        payload={payload}
        open={entered}
        onEnter={() => setEntered(true)}
        onRsvp={handleRsvp}
        rsvpSubmitted={updateRSVP.isSuccess}
        rsvpClosed={rsvpClosed}
        coordinador={{ url: coordinadorUrl, nombre: guest?.coordinator_name }}
        album={albumSlot}
      />
      {/* Crédito de Growists — solo en la invitación pública; usa los tokens del estilo. */}
      {entered && (
        <div className={themeClass(payload.estilo)}>
          <footer
            style={{
              textAlign: 'center',
              padding: '36px 16px',
              background: 'var(--bg-elevated)',
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-display)',
              fontSize: 14,
              letterSpacing: '0.02em',
            }}
          >
            Hecha con <span style={{ color: 'var(--accent)' }}>♥</span> por{' '}
            <a
              href="https://growists.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--fg-primary)', fontWeight: 600, textDecoration: 'underline' }}
            >
              Growists
            </a>
          </footer>
        </div>
      )}
    </>
  );
};

export default PublicInvitation;
